import { truncateDecimals } from '@lair-finance/util'
import { formatEther, parseEther } from 'ethers'
import { Address } from 'viem'

export const commafy = (value: number | string): string => {
  const num = Number(value)
  if (isNaN(num)) {
    return value.toString()
  }

  if (num === 0) {
    return '0'
  }

  const [integerPart, decimalPart] = value.toString().split('.')
  const isNegative = integerPart.startsWith('-')
  const absInteger = isNegative ? integerPart.slice(1) : integerPart

  const withCommas = absInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const finalInteger = isNegative ? `-${withCommas}` : withCommas
  return decimalPart ? `${finalInteger}.${decimalPart}` : finalInteger
}

export const formatNumberToMillion = (num: number, decimalPlaces: number = 1) => {
  if (num >= 1_000_000) {
    return parseFloat(Math.floor(num / 1_000_000).toFixed(decimalPlaces)) + 'M'
  }
  return truncateDecimals(num, decimalPlaces)
}

export const formatNumberToKilo = (num: number, decimalPlaces: number = 1): string => {
  if (num >= 1_000) {
    return parseFloat(Math.floor(num / 1_000).toFixed(decimalPlaces)) + 'K'
  }
  return truncateDecimals(num, decimalPlaces)
}

export const formatTVL = (raw: number | string, decimalPlaces: number = 2) => {
  const numericValue = typeof raw === 'string' ? parseFloat(raw) : raw

  if (numericValue >= 1_000_000) {
    const value = truncateDecimals(numericValue / 1_000_000, decimalPlaces)
    const unit = 'M'
    const formatted = value + unit

    return { value, unit, formatted }
  }

  if (numericValue >= 1_000) {
    const value = truncateDecimals(numericValue / 1_000, 2)
    const unit = 'K'
    const formatted = value + unit

    return { value, unit, formatted }
  }

  const value = truncateDecimals(numericValue, decimalPlaces)
  const unit = ''
  const formatted = value + unit

  return { value, unit, formatted }
}

export function formatEtherToDecimal(wei: bigint, decimals = 4) {
  const ether = formatEther(wei)
  return truncateDecimals(ether, decimals)
}

export function normalizeAddress(address: Address | string) {
  return address.toLowerCase().trim() as Address
}

export function displayAmount(amount: string, options?: { decimals?: number; threshold?: string }): string {
  const threshold = options?.threshold ?? '0.00000001'
  const thresholdBigInt = parseEther(threshold)
  const parsedAmount = parseEther(amount)

  if (parsedAmount === 0n) return '0'

  if (parsedAmount <= thresholdBigInt) return `< ${threshold}`

  if (options?.decimals) return truncateDecimals(amount, options.decimals)

  return amount
}

export function formatPrice(price: string | number) {
  const priceNumber = Number(price)

  // ≥ 1 B
  if (priceNumber >= 1_000_000_000) {
    return `${Math.floor(priceNumber / 1_000_000_000)}B`
  }

  // ≥ 1 M
  if (priceNumber >= 1_000_000) {
    return `${Math.floor(priceNumber / 1_000_000)}M`
  }

  // ≥ 1 K
  if (priceNumber >= 1_000) {
    const a = Math.floor(priceNumber / 1_000)
    const b = Math.floor((priceNumber % 1_000) / 100)
    return b > 0 ? `${a}.${b}K` : `${a}K`
  }

  // 100 – 999.9 : 소수점 1자리
  if (priceNumber >= 100) {
    const scaled = Math.floor(priceNumber * 10)
    const intPart = Math.floor(scaled / 10)
    const decimal = scaled % 10
    return decimal > 0 ? `${intPart}.${decimal}` : intPart.toString()
  }

  // 10 – 99.99 : 소수점 2자리
  if (priceNumber >= 10) {
    const scaled = Math.floor(priceNumber * 100)
    const intPart = Math.floor(scaled / 100)
    const decimal = scaled % 100
    const padded = decimal < 10 ? `0${decimal}` : `${decimal}`
    return decimal > 0 ? `${intPart}.${padded}` : intPart.toString()
  }

  // 0 – 9.999 : 소수점 3자리
  const scaled = Math.floor(priceNumber * 1000)
  const intPart = Math.floor(scaled / 1000)
  const decimal = scaled % 1000
  const padded = decimal < 10 ? `00${decimal}` : decimal < 100 ? `0${decimal}` : `${decimal}`

  return decimal > 0 ? `${intPart}.${padded}` : intPart.toString()
}
